// NIGHT-DIRECTSTAFF-2026-10-16 §1: "Needs a human" -- every lead that replied and has had no human follow-up, newest
// reply first (lib/needsHuman.js). One tap each: Call (the number appears only then), Text (opens the agents window's
// compose box, StaffText.jsx, which sends through their CRM route, CRM-TEXT-2026-10-16 -- never an sms: link; "coming
// soon" until that route is live), Mark handled (the lead's own handled mark, as the Leads view writes it; a
// conversation with no lead is marked "handled off-system").
// NEEDS-HUMAN-FIX-2026-10-16: each row says, without a tap, who they are (name, discipline, state), how they came in and
// when, what they asked for, their last reply and its time, and how long they have waited; the id is a small grey tag.
// The email is on the row at desktop width. Tapping the row opens the full lead: email, phone (on tap), the whole
// conversation, and the same Call / Text / Mark handled buttons.
import { useEffect, useRef, useState } from 'react';
import { waitingText } from './lib/needsHuman';
import { EmailButton } from './EmailCompose';
import { deliveryLine, TONE_COLOR } from './lib/appPolish';
import { telHref } from './lib/facilityPortal';                     // NIGHT-DIRECTSTAFF-2026-10-24 §7: a shown number is a tel: link
import SnoozeControl, { AgeChip } from './SnoozeControl';
import { overdueCount, needsTitle } from './lib/snooze';   // 10-28 §2              // NIGHT-DIRECTSTAFF-2026-10-25c §1
import { untilText } from './lib/snooze';

const STYLE = `
.nh-row { border-bottom: 1px solid #f1f5f9; padding: 10px 12px; font-size: 13px; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px 14px; align-items: start; }
.nh-open { display: block; width: 100%; text-align: left; padding: 2px 0; min-height: 44px; color: #0f172a; font-size: 13px; }
.nh-open:hover .nh-name { text-decoration: underline; }
.nh-open > span { display: block; }
.nh-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; }
.nh-name { font-size: 15px; font-weight: 700; color: #0f172a; }
.nh-id { font-family: monospace; font-size: 11px; color: #475569; background: #f1f5f9; border-radius: 4px; padding: 0 4px; }
.nh-meta { color: #334155; margin-top: 1px; }
.nh-email-wide { color: #475569; margin-left: 8px; }
.nh-line { color: #334155; margin-top: 3px; overflow-wrap: anywhere; }
.nh-label { font-weight: 700; color: #475569; }
.nh-clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.nh-actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.nh-btn { min-height: 44px; min-width: 64px; border-radius: 10px; padding: 0 14px; font-size: 14px; font-weight: 700; cursor: pointer; border: 0; }
.nh-detail { grid-column: 1 / -1; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; }
.nh-detail dl { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 4px 12px; margin: 0 0 10px; }
.nh-detail dt { font-weight: 700; color: #475569; }
.nh-detail dd { margin: 0; overflow-wrap: anywhere; }
.nh-thread { list-style: none; padding: 0; margin: 0; display: grid; gap: 6px; }
.nh-msg { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 8px; overflow-wrap: anywhere; white-space: pre-wrap; }
.nh-msg.in { border-left: 3px solid #0e7490; }
.nh-msg-who { font-size: 11px; font-weight: 700; color: #475569; white-space: normal; }
@media (max-width: 760px) {
  .nh-row { grid-template-columns: 1fr; }
  .nh-email-wide { display: none; }
  .nh-actions { justify-content: stretch; }
  .nh-actions .nh-btn { flex: 1 1 0; }
}
`;
const fmt = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const telOf = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d.length === 10 ? `+1${d}` : d.length === 11 && d.startsWith('1') ? `+${d}` : d ? `+${d}` : '';
};
const ASKED_LABEL = { job: 'Applied to', form: 'Asked', voicemail: 'Voicemail' };
const WHO = { candidate: 'Them', user: 'Them', agent: 'Agent', human: 'Staff', assistant: 'Agent' };

/**
 * rows        needsHumanRows(...)
 * textReady   the CRM text route answers (staff.textReady);  onText(row) opens the compose box (StaffText.jsx)
 * textBlock(row) -> '' or why this row can't be texted ('Opted out', 'No lead record', 'No textable number')
 * onMarkHandled(row) -> Promise;  dial(tel) opens the dialer (window.location in the app)
 * loadThread(conversationId) -> Promise<messages> (the whole conversation, for the open row)
 */
const defaultBlock = (r) => (r.optOut ? 'Opted out' : !r.leadId ? 'No lead record' : '');
// EMAIL-CRM-2026-10-22: onEmail(row) opens the Email box (from Matt, never a mail app); emailOptedOut(row) -> "No email"
export default function NeedsHumanView({ rows, loadedAt, refreshing = false, onRefresh = () => {}, textReady = false,
  textBlock = defaultBlock, onText = () => {}, onMarkHandled, toast = () => {}, dial = (tel) => { window.location.href = `tel:${tel}`; },
  loadThread = async () => [], onHandBack = null, top = null, topCount = 0, topOverdue = 0, onEmail = null, emailOptedOut = () => false,
  onSnooze = null, snoozedCount = 0, lastSnooze = null, onUndoSnooze = () => {} }) {
  const [handedBack, setHandedBack] = useState({});     // CRM-LEADS §3: conversationId -> true once handed back here
  const [revealed, setRevealed] = useState(null);       // the one row whose number is on screen
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);               // the one row whose full lead is open
  const [threads, setThreads] = useState({});           // conversationId -> messages | 'loading' | { error }
  // CRM-LEADS-2026-10-16 §2: the open lead's thread follows the live refresh -- a newer reply reloads it, no reopening
  const threadAt = useRef({});                           // conversationId -> the lastReplyAt its thread was loaded at
  const openRow = rows.find((r) => r.key === open);
  const openReplyAt = openRow ? openRow.lastReplyAt : null;
  useEffect(() => {
    if (!openRow) return;
    const id = openRow.conversationId;
    if (!threadAt.current[id] || threadAt.current[id] === openReplyAt) return;
    threadAt.current[id] = openReplyAt;
    loadThread(id).then((msgs) => setThreads((t) => ({ ...t, [id]: msgs || [] }))).catch(() => {});
  }, [openReplyAt]);   // eslint-disable-line

  const call = (r) => { setRevealed(r.key); const tel = telOf(r.phone); if (tel) dial(tel); };
  const handled = async (r) => {
    setBusy(r.key);
    try { await onMarkHandled(r); toast('Marked handled'); } catch (e) { toast(e.message || 'Could not mark handled'); } finally { setBusy(null); }
  };
  const toggle = async (r) => {
    if (open === r.key) { setOpen(null); return; }
    setOpen(r.key);
    if (threads[r.conversationId] && threads[r.conversationId] !== 'loading' && !threads[r.conversationId].error) return;
    setThreads((t) => ({ ...t, [r.conversationId]: 'loading' }));
    threadAt.current[r.conversationId] = r.lastReplyAt;
    try {
      const msgs = await loadThread(r.conversationId);
      setThreads((t) => ({ ...t, [r.conversationId]: msgs || [] }));
    } catch (e) {
      setThreads((t) => ({ ...t, [r.conversationId]: { error: e.message || 'Could not load the conversation' } }));
    }
  };

  return (
    <div className="fade-in" data-testid="needs-human-view">
      <style>{STYLE}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        {/* NIGHT-DIRECTSTAFF-2026-10-28 §2: the count and how many are overdue (24 h on the clock), snoozed ones never in either */}
        {(() => { const over = overdueCount(rows.map((r) => r.lastReplyAt)) + topOverdue; return (
        <h2 data-testid="nh-title" aria-label={needsTitle(rows.length + topCount, over)} style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>
          Needs a human · <span data-testid="nh-count" style={{ color: '#b91c1c' }}>{rows.length + topCount}</span>
          {over > 0 && <> · <span data-testid="nh-overdue" style={{ color: '#b91c1c' }}>{over} overdue</span></>}
        </h2>); })()}
        <button className="nb" data-testid="nh-refresh" onClick={onRefresh} disabled={refreshing}
          style={{ fontSize: 13, color: '#1d4ed8', minHeight: 44, padding: '0 6px' }}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
      </div>
      <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>
        Leads who replied and haven't heard from a person yet, oldest first. OVERDUE after 24 hours. Tap one for the full lead and the conversation.
        {loadedAt ? ` Updated ${fmt(loadedAt.toISOString ? loadedAt.toISOString() : loadedAt)}.` : ''}
      </p>
      {/* NIGHT-DIRECTSTAFF-2026-10-25c §1: what is snoozed (hidden until its time; the badge leaves it out), and Undo for the last one */}
      {(snoozedCount > 0 || lastSnooze) && (
        <div data-testid="nh-snoozed" role="status" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: '#334155', margin: '0 0 10px' }}>
          {snoozedCount > 0 && <span>{snoozedCount} snoozed, hidden until their time.</span>}
          {lastSnooze && <span data-testid="nh-snoozed-last">{lastSnooze.name}: {untilText(lastSnooze.until)}.</span>}
          {lastSnooze && <button className="nb" data-testid="nh-snooze-undo" onClick={onUndoSnooze} style={{ background: '#0f172a', color: 'white', borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700 }}>Undo</button>}
        </div>)}
      {/* NIGHT-DIRECTSTAFF-2026-10-21 §1/§2: facility requests and call-only candidates, above the replies */}
      {top}
      {rows.length === 0 && topCount > 0 ? null : rows.length === 0 ? (
        <div data-testid="nh-empty" className="card" style={{ padding: 24, textAlign: 'center', fontSize: 15, color: '#15803d', fontWeight: 600 }}>
          Nobody is waiting on a person right now.
        </div>
      ) : (
        <div className="card" data-testid="nh-list" style={{ padding: 0, overflow: 'hidden' }}>
          {rows.map((r) => {
            const who = r.name || 'Name not given';
            const origin = r.origin || { label: r.source || 'no lead record', at: null };
            const asked = r.asked && r.asked.text && r.asked.from !== 'text' ? r.asked : null;   // "their last text" is the next line
            const isOpen = open === r.key;
            const thread = threads[r.conversationId];
            return (
              <div key={r.key} className="nh-row" data-testid={`nh-row-${r.shortId}`} data-kbd-row>
                <button className="nb nh-open" data-kbd-open data-testid={`nh-open-${r.shortId}`} aria-expanded={isOpen} aria-controls={`nh-detail-${r.shortId}`}
                  onClick={() => toggle(r)}>
                  <span className="nh-head">
                    <span className="nh-name" data-testid={`nh-name-${r.shortId}`}>{who}</span>
                    <span className="nh-id" data-testid={`nh-id-${r.shortId}`} title="Lead id (first 8 characters)">{r.shortId}</span>
                    {/* NIGHT-DIRECTSTAFF-2026-10-28 §2: the age is the hours chip ("52 h", OVERDUE); days only when there is no reply time */}
                    {!r.lastReplyAt && <span data-testid={`nh-wait-${r.shortId}`} style={{ fontSize: 12, fontWeight: 700, color: r.daysWaiting >= 1 ? '#b91c1c' : '#92400e' }}>
                      Waiting {waitingText(r.daysWaiting)}</span>}
                    {r.lastReplyAt && <AgeChip testid={`nh-age-${r.shortId}`} since={r.lastReplyAt} />}
                  </span>
                  <span className="nh-meta" data-testid={`nh-meta-${r.shortId}`}>
                    {[r.discipline, r.state].filter(Boolean).join(' · ') || 'Discipline and state not given'}
                    {r.email && <span className="nh-email-wide" data-testid={`nh-email-row-${r.shortId}`}>{r.email}</span>}
                  </span>
                  <span className="nh-line" data-testid={`nh-origin-${r.shortId}`}>
                    <span className="nh-label">Came in: </span>{origin.label}{origin.at ? ` · ${day(origin.at)}` : ''}</span>
                  {asked && <span className="nh-line nh-clamp" data-testid={`nh-asked-${r.shortId}`}>
                    <span className="nh-label">{ASKED_LABEL[asked.from]}: </span>{asked.text}</span>}
                  <span className="nh-line nh-clamp" data-testid={`nh-last-${r.shortId}`}>
                    <span className="nh-label">Last reply {fmt(r.lastReplyAt)}: </span>
                    {(r.lastReply && r.lastReply.body) || <em style={{ color: '#475569' }}>no text</em>}</span>
                </button>
                <div className="nh-actions">
                  <button className="nh-btn" data-testid={`nh-call-${r.shortId}`} disabled={!r.phone} onClick={() => call(r)}
                    aria-label={`Call ${who}`} style={{ background: '#166534', color: '#fff', opacity: r.phone ? 1 : 0.45 }}>Call</button>
                  {(() => { const block = textReady ? textBlock(r) : ''; const off = !textReady || !!block; return (
                  <button className="nh-btn" data-testid={`nh-text-${r.shortId}`} disabled={off} onClick={() => onText(r)}
                    aria-label={!textReady ? 'Text: coming soon' : block ? `${who}: ${block}` : `Text ${who}`}
                    style={{ background: '#1d4ed8', color: '#fff', opacity: off ? 0.45 : 1 }}>
                    {!textReady ? 'Text — coming soon' : block || 'Text'}</button>
                  ); })()}
                  <button className="nh-btn" data-testid={`nh-handled-${r.shortId}`} disabled={busy === r.key} onClick={() => handled(r)}
                    aria-label={`Mark ${who} handled`} style={{ background: '#e2e8f0', color: '#0f172a' }}>
                    {busy === r.key ? 'Saving…' : 'Mark handled'}</button>
                  {onSnooze && <SnoozeControl testid={`nh-snooze-${r.shortId}`} onSnooze={async (until) => { try { await onSnooze(r, until); toast(untilText(until)); } catch (e) { toast(e.message || 'Not snoozed'); } }} />}
                </div>
                {revealed === r.key && r.phone && !isOpen && (
                  <a data-testid={`nh-phone-${r.shortId}`} href={telHref(r.phone) || undefined} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, fontSize: 14, fontWeight: 700, color: '#1d4ed8' }}>{r.phone}</a>
                )}
                {isOpen && (
                  <div className="nh-detail" id={`nh-detail-${r.shortId}`} data-testid={`nh-detail-${r.shortId}`}>
                    <dl>
                      <dt>Email</dt>
                      <dd data-testid={`nh-email-${r.shortId}`}>{r.email ? (
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minHeight: 44 }}>
                          <span style={{ overflowWrap: 'anywhere' }}>{r.email}</span>
                          {onEmail && <EmailButton testid={`nh-email-btn-${r.shortId}`} email={r.email} optedOut={emailOptedOut(r)} onOpen={() => onEmail(r)} />}
                        </span>) : 'Not given'}</dd>
                      <dt>Phone</dt>
                      <dd>{revealed === r.key && r.phone
                        ? <a data-testid={`nh-phone-${r.shortId}`} href={telHref(r.phone) || undefined} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, fontWeight: 700, color: '#1d4ed8' }}>{r.phone}</a>
                        : r.phone
                          ? <button className="nb" data-testid={`nh-show-phone-${r.shortId}`} onClick={() => setRevealed(r.key)}
                            style={{ color: '#1d4ed8', padding: 0, minHeight: 44, fontSize: 13 }}>Show phone</button>
                          : 'Not given'}</dd>
                      <dt>Came in</dt><dd>{origin.label}{origin.at ? ` · ${fmt(origin.at)}` : ''}</dd>
                      {r.asked && r.asked.text && <><dt>{ASKED_LABEL[r.asked.from] || 'Said'}</dt><dd data-testid={`nh-asked-full-${r.shortId}`}>{r.asked.text}</dd></>}
                      <dt>Id</dt><dd style={{ fontFamily: 'monospace', color: '#475569' }}>{r.leadId || r.conversationId}</dd>
                    </dl>
                    {r.takeover && onHandBack && !handedBack[r.conversationId] && (
                      <p style={{ margin: '0 0 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span data-testid={`nh-yours-${r.shortId}`} style={{ color: '#92400e', fontWeight: 700 }}>You have this one.</span>
                        <button className="nh-btn" data-testid={`nh-hand-back-${r.shortId}`} style={{ background: '#92400e', color: '#fff' }}
                          onClick={async () => {
                            try { await onHandBack(r); setHandedBack((h) => ({ ...h, [r.conversationId]: true })); toast('Handed back to the agent'); }
                            catch (e) { toast(e.message || 'Could not hand back to the agent'); }
                          }}>Hand back to agent</button>
                      </p>
                    )}
                    {handedBack[r.conversationId] && <p data-testid={`nh-handed-back-${r.shortId}`} style={{ margin: '0 0 10px', color: '#166534' }}>Handed back: the agent picks up from here.</p>}
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 6px' }}>Conversation</p>
                    {thread === 'loading' || thread === undefined ? <p style={{ color: '#475569', margin: 0 }}>Loading…</p>
                      : thread.error ? <p data-testid={`nh-thread-error-${r.shortId}`} style={{ color: '#b91c1c', margin: 0 }}>{thread.error}</p>
                        : thread.length === 0 ? <p style={{ color: '#475569', margin: 0 }}>No messages.</p>
                          : (
                            <ol className="nh-thread" data-testid={`nh-thread-${r.shortId}`}>
                              {thread.map((m) => {
                                const inbound = m.role === 'candidate' || m.role === 'user';
                                return (
                                  <li key={m.id || `${m.created_at}-${m.role}`} className={`nh-msg${inbound ? ' in' : ''}`}>
                                    <span className="nh-msg-who">{inbound ? (r.name ? r.name.split(' ')[0] : WHO.candidate) : (WHO[m.role] || m.role)} · {fmt(m.created_at)}</span>
                                    {'\n'}{m.body}
                                    {m.role === 'human' && (() => { const dl = deliveryLine(m); return <span data-testid="nh-msg-delivery" style={{ display: 'block', fontSize: 11, color: TONE_COLOR[dl.tone] }}>{dl.text}</span>; })()}
                                  </li>
                                );
                              })}
                            </ol>
                          )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
